"use client";

// CommandHotkeys binds every registry command's global hotkey to a
// single document-level keydown listener. We rebuild the lookup
// table whenever the CommandContext changes — that's how scope-
// dependent hotkeys (⌘A only fires on a load detail page) stay
// out of each other's way.
//
// Runtime conflict detection: if two eligible commands resolve to
// the same canonicalKey under the current context, we emit a
// console.warn. The static CI check catches scope-identical
// conflicts; this catches the scope-mismatch-makes-it-look-unique-
// statically case described in command-bar-todo.md §3.
import * as React from "react";
import { commands } from "@/lib/commands/registry";
import { useCommandContext } from "@/lib/commands/context";
import { eligible } from "@/lib/commands/filter";
import {
  canonicalKey,
  isTypingInInput,
  matchEvent,
} from "@/lib/commands/hotkey";
import { invokeCommand, dispatchOpenPanel } from "@/lib/commands/handler";
import type { Command } from "@/lib/commands/types";

export function CommandHotkeys() {
  const ctx = useCommandContext();
  const ctxRef = React.useRef(ctx);
  React.useEffect(() => {
    ctxRef.current = ctx;
  }, [ctx]);

  const bindings = React.useMemo(() => {
    const map = new Map<string, Command>();
    for (const cmd of commands) {
      if (!cmd.hotkey || cmd.hotkey.whenOpen) continue;
      if (!eligible(cmd, ctx)) continue;
      const key = canonicalKey(cmd.hotkey);
      const prior = map.get(key);
      if (prior) {
        // Runtime warn; CI catches the static case.
        console.warn(
          `[command-bar] hotkey conflict: ${prior.id} and ${cmd.id} both want ${key}`,
        );
        continue;
      }
      map.set(key, cmd);
    }
    return map;
  }, [ctx]);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isTypingInInput(e.target)) return;
      // The ? cheatsheet shortcut. Shift+/ on most layouts.
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        ctxRef.current.router.push("/keyboard-shortcuts");
        return;
      }
      // Walk the bindings; we want O(n) over the bound subset, not
      // O(commands) over the whole registry, because the registry
      // is bigger and most of it is scope-inelegible at any given
      // moment.
      for (const [, cmd] of bindings) {
        if (!cmd.hotkey) continue;
        if (matchEvent(cmd.hotkey, e)) {
          e.preventDefault();
          if (cmd.handler.kind === "panel") {
            dispatchOpenPanel({
              panel: cmd.handler.open,
              ctxSnapshot: ctxRef.current,
            });
          } else {
            // Note: confirmation dialogs are skipped for hotkey
            // invocations to preserve speed. Destructive
            // commands that require confirmation should not have
            // a hotkey assigned (lint should be extended to
            // enforce this; for now it's a convention).
            void invokeCommand(cmd, ctxRef.current, "hotkey");
          }
          return;
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [bindings]);

  return null;
}
